import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { experienceService } from '@/services/experience.service';
import { customerService } from '@/services/customer.service';
import { useCart } from '@/context/CartContext';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { ActiveSearchChips, normText } from '@/components/public/ActiveSearchChips';
import { Seo } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { AddToCartButton } from '@/components/cart/AddToCartButton';
import { FavoriteButton } from '@/components/public/FavoriteButton';
import { Sparkles, MapPin, Clock, ArrowRight, ImageIcon, Ticket, Calendar, Users, GraduationCap, Bike, Map as MapIcon } from 'lucide-react';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }); } catch { return iso; } };

const HERO = 'https://images.pexels.com/photos/37406898/pexels-photo-37406898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920';
const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 })}`;
const CATEGORIES = ['Escursioni', 'Storico culturali', 'Natura', 'Kids', 'Tour', 'Benessere', 'Olistico', 'Avventura', 'Gusto'];

const TYPE = {
  single: { label: 'Evento', icon: Calendar },
  group: { label: 'Gruppo', icon: Users },
  course: { label: 'Corso', icon: GraduationCap },
  tour: { label: 'Tour', icon: MapIcon },
  vehicle: { label: 'Con mezzo', icon: Bike },
  rental: { label: 'Noleggio', icon: Bike },
};

function ExpCard({ e, isFav, onFav }) {
  const tm = TYPE[e.type] || TYPE.single;
  const TypeIcon = tm.icon;
  return (
    <Link to={`/esperienze/${e.slug || e.id}`} className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`catalog-card-${e.id}`}>
      <div className="relative h-52 overflow-hidden bg-muted">
        {e.cover_image ? (
          <img src={mediaUrl(e.cover_image)} alt={e.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-9 w-9 text-muted-foreground" /></div>
        )}
        <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground shadow-sm backdrop-blur"><TypeIcon className="h-3 w-3 text-primary" />{tm.label}</span>
        <FavoriteButton structureId={e.id} structureType="experience" isFavorite={isFav} onChange={onFav} className="absolute left-3 top-3" size="sm" />
        <span className="absolute bottom-3 right-3 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur">da {euro(e.from_price)}{e.is_rental ? '/g' : ''}</span>
      </div>
      <div className="p-5">
        {e.category && <span className="text-xs font-medium uppercase tracking-wide text-primary">{e.category}</span>}
        <h3 className="mt-1 font-display text-xl font-semibold text-foreground line-clamp-1">{e.title}</h3>
        {e.owner_name && (
          <p className="mt-0.5 text-xs text-muted-foreground">di <span className="font-medium text-foreground/80">{e.owner_name}</span></p>
        )}
        {e.short_desc && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{e.short_desc}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {e.location?.name && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{e.location.name}</span>}
          {e.duration_min > 0 && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />{e.duration_min} min</span>}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
          <span className="flex items-center gap-1 text-sm font-medium text-primary">Scopri <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
          {e.from_price > 0 && (
            <AddToCartButton catalogId={`experience:${e.id}`} title={e.title} label="Aggiungi" size="sm" variant="secondary" stopPropagation className="h-8" />
          )}
        </div>
      </div>
    </Link>
  );
}

export default function ExperienceCatalog() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState('tutte');
  const [favSet, setFavSet] = useState(() => new Set());
  const { isCustomer } = useCart() || {};
  const [params, setParams] = useSearchParams();
  const loc = params.get('loc') || '';
  const dateParam = params.get('date') || '';

  useEffect(() => {
    (async () => {
      try { setData(await experienceService.publicList()); }
      catch { setData({ experiences: [], categories: [] }); }
    })();
  }, []);

  // Preferiti del cliente (solo se loggato come customer) per lo stato iniziale del cuore.
  useEffect(() => {
    if (!isCustomer) { setFavSet(new Set()); return; }
    (async () => {
      try {
        const res = await customerService.getFavorites();
        const ids = (res?.favorites || [])
          .filter((f) => f.structure_type === 'experience')
          .map((f) => f.structure_id);
        setFavSet(new Set(ids));
      } catch { /* non bloccante */ }
    })();
  }, [isCustomer]);

  const onFav = (id, fav) => {
    setFavSet((prev) => {
      const next = new Set(prev);
      if (fav) next.add(id); else next.delete(id);
      return next;
    });
  };

  const orderedCats = useMemo(() => {
    const present = new Set((data?.experiences || []).map((e) => e.category).filter(Boolean));
    const base = CATEGORIES.filter((c) => present.has(c));
    const extra = Array.from(present).filter((c) => !CATEGORIES.includes(c));
    return [...base, ...extra];
  }, [data]);

  const grouped = useMemo(() => {
    const items = data?.experiences || [];
    const groups = orderedCats
      .map((c) => ({ cat: c, items: items.filter((e) => e.category === c) }))
      .filter((g) => g.items.length);
    const uncategorized = items.filter((e) => !e.category);
    if (uncategorized.length) groups.push({ cat: 'Altre esperienze', items: uncategorized });
    return groups;
  }, [data, orderedCats]);

  const filtered = useMemo(() => {
    const items = data?.experiences || [];
    if (cat === '__noleggio__') return items.filter((e) => e.is_rental || e.type === 'rental');
    return items.filter((e) => e.category === cat);
  }, [data, cat]);

  const hasRentals = useMemo(() => (data?.experiences || []).some((e) => e.is_rental || e.type === 'rental'), [data]);

  const locFiltered = useMemo(() => {
    const items = data?.experiences || [];
    if (!loc) return items;
    const q = normText(loc);
    return items.filter((e) => normText([e.title, e.location?.name, e.location?.address, e.category, e.owner_name].join(' ')).includes(q));
  }, [data, loc]);

  const chips = [];
  if (loc) chips.push({ icon: MapPin, text: loc });
  if (dateParam) chips.push({ icon: Calendar, text: fmtDate(dateParam) });
  const clearFilters = () => setParams({}, { replace: true });

  const gridCls = 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <PublicLayout>
      <Seo title="Esperienze &amp; Eventi — Sila Experiences" description="Prenota escursioni, eventi, corsi ed esperienze nel cuore della Sila. Biglietti immediati con QR." />
      <SilaHero image={HERO} badge="Esperienze" title="Vivi la Sila" subtitle="Escursioni, eventi e corsi con guide del territorio. Scegli, acquista e ricevi subito il tuo biglietto con QR." icon={Sparkles} />

      <section className="sila-section py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Il catalogo</h2>
            <p className="mt-1 text-sm text-muted-foreground">Esplora per macro-area oppure filtra per categoria.</p>
          </div>
          {!loc && orderedCats.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="catalog-categories">
              <button type="button" onClick={() => setCat('tutte')} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${cat === 'tutte' ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent'}`} data-testid="catalog-cat-tutte">Tutte</button>
              {hasRentals && (
                <button type="button" onClick={() => setCat('__noleggio__')} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${cat === '__noleggio__' ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent'}`} data-testid="catalog-cat-noleggio"><Bike className="h-3.5 w-3.5" />Noleggio</button>
              )}
              {orderedCats.map((c) => (
                <button key={c} type="button" onClick={() => setCat(c)} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${cat === c ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent'}`} data-testid={`catalog-cat-${c}`}>{c}</button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <ActiveSearchChips chips={chips} onClear={clearFilters} resultCount={locFiltered.length} noun="esperienze" />
          {data === null ? (
            <div className={gridCls}>{[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 w-full rounded-2xl" />)}</div>
          ) : loc ? (
            locFiltered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="catalog-empty">
                <Ticket className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-3 font-medium text-foreground">Nessuna esperienza trovata per “{loc}”</p>
                <p className="text-sm text-muted-foreground">Prova a rimuovere i filtri per vedere tutte le esperienze.</p>
              </div>
            ) : (
              <div className={gridCls} data-testid="catalog-grid">
                {locFiltered.map((e) => <ExpCard key={e.id} e={e} isFav={favSet.has(e.id)} onFav={onFav} />)}
              </div>
            )
          ) : (data.experiences || []).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="catalog-empty">
              <Ticket className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-medium text-foreground">Nessuna esperienza disponibile al momento</p>
              <p className="text-sm text-muted-foreground">Torna presto: nuove esperienze vengono pubblicate di continuo.</p>
            </div>
          ) : cat === 'tutte' ? (
            <div className="space-y-12" data-testid="catalog-grouped">
              {grouped.map((g) => (
                <div key={g.cat} data-testid={`catalog-section-${g.cat}`}>
                  <div className="mb-4 flex items-center gap-3">
                    <h3 className="font-display text-xl font-semibold text-foreground">{g.cat}</h3>
                    <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{g.items.length}</span>
                    <button type="button" onClick={() => setCat(g.cat)} className="ml-auto flex items-center gap-1 text-sm font-medium text-primary hover:underline">Vedi tutte <ArrowRight className="h-4 w-4" /></button>
                  </div>
                  <div className={gridCls}>
                    {g.items.map((e) => <ExpCard key={e.id} e={e} isFav={favSet.has(e.id)} onFav={onFav} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="catalog-empty">
              <Ticket className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-medium text-foreground">Nessuna esperienza in questa categoria</p>
            </div>
          ) : (
            <div className={gridCls} data-testid="catalog-grid">
              {filtered.map((e) => <ExpCard key={e.id} e={e} isFav={favSet.has(e.id)} onFav={onFav} />)}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
